import React, { useEffect } from 'react';
import '../LandingPage.css';
import mbIcon from '../../assets/mb_icon.png';

interface AIPoweredSchoolManagementProps {
  onBack: () => void;
  onNavigateToLogin: () => void;
  onNavigateToContact: () => void;
}

const AIPoweredSchoolManagement: React.FC<AIPoweredSchoolManagementProps> = ({ onBack, onNavigateToLogin, onNavigateToContact }) => {
  useEffect(() => {
    document.title = 'AI-Powered School Management Software | Automate & Optimize | My Learning Board';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Transform school operations with AI-powered management software. Automate attendance, fees, timetable, grading, and parent communication. Smart analytics, predictive insights & 24/7 AI assistant.');
    }
    return () => { document.title = 'My Learning Board – AI-Powered School Management Software & ERP in India'; };
  }, []);

  const aiFeatures = [
    { icon: 'bi-robot', title: 'AI Assistant (24/7)', desc: 'Instant answers for parents, teachers & admin. Handles queries on fees, attendance, timetable and more — no human needed.', color: '#6610F2' },
    { icon: 'bi-graph-up-arrow', title: 'Predictive Analytics', desc: 'AI identifies at-risk students before they fall behind. Predict fee defaults, attendance drops & academic decline early.', color: '#E53935' },
    { icon: 'bi-calendar-check', title: 'Smart Timetable', desc: 'AI auto-generates conflict-free timetables in seconds, balancing teacher workload and room availability.', color: '#28A745' },
    { icon: 'bi-file-earmark-text', title: 'Auto Report Cards', desc: 'Generate consolidated report cards with subject grouping, best-of-N rules and weighted exam scores — one click.', color: '#FB8C00' },
    { icon: 'bi-person-badge', title: 'AI Resume Screening', desc: 'Upload CVs for recruitment and let AI extract qualifications, experience & skills automatically.', color: '#2D7DD2' },
    { icon: 'bi-chat-dots', title: 'Natural Language Queries', desc: 'Ask "Show me Class 10 students with attendance below 75%" and get instant results — no coding needed.', color: '#D81B60' },
  ];

  const workflows = [
    { step: '01', title: 'Automate Daily Tasks', desc: 'Attendance marking, fee reminders, homework distribution — all automated with AI triggers.' },
    { step: '02', title: 'Analyze & Predict', desc: 'AI crunches your school data to surface trends, anomalies and actionable predictions.' },
    { step: '03', title: 'Personalize Learning', desc: 'Every student gets AI-powered insights on strengths, weaknesses and recommended focus areas.' },
    { step: '04', title: 'Communicate Smarter', desc: 'AI drafts announcements, translates messages and sends them via WhatsApp, SMS & email.' },
  ];

  const comparisons = [
    { feature: 'Attendance Tracking', traditional: 'Manual register / biometric only', ai: 'Auto-tracking + absence prediction + parent alerts' },
    { feature: 'Fee Collection', traditional: 'Bill generation + manual follow-up', ai: 'Smart reminders + default prediction + auto reconciliation' },
    { feature: 'Timetable Creation', traditional: '2-3 days of manual effort', ai: 'AI generates in 30 seconds, conflict-free' },
    { feature: 'Report Cards', traditional: 'Subject-wise entry, manual calculation', ai: 'Auto-consolidated with best-of-N, weighted scores' },
    { feature: 'Parent Communication', traditional: 'Circular / SMS blast', ai: 'Personalized AI messages via WhatsApp in parent\'s language' },
    { feature: 'Student Analytics', traditional: 'End-of-term review only', ai: 'Real-time dashboards with predictive alerts' },
  ];

  return (
    <div className="landing-page min-vh-100">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container-fluid px-4">
          <a className="navbar-brand d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <img src={mbIcon} alt="My Learning Board" className="mb-logo" />
            <span className="ms-2 fw-bold text-primary-custom">My Learning Board</span>
          </a>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary-custom" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Home
            </button>
            <button className="btn btn-primary-custom text-white" onClick={onNavigateToLogin}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #6610F2 0%, #5C2597 50%, #2D7DD2 100%)' }}>
        <div className="container-fluid px-4">
          <div className="row align-items-center py-5">
            <div className="col-lg-7">
              <span className="badge bg-white text-dark mb-3 px-3 py-2">
                <i className="bi bi-stars me-2"></i>Next-Generation School ERP
              </span>
              <h1 className="display-4 fw-bold mb-4">
                AI-Powered School Management Software
              </h1>
              <p className="lead mb-4 opacity-90">
                Stop managing spreadsheets. Let AI handle attendance, fees, timetables, report cards and parent communication —
                while you focus on what matters: education.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <button className="btn btn-light btn-lg px-4" onClick={onNavigateToContact}>
                  <i className="bi bi-calendar-event me-2"></i>Book Free Demo
                </button>
                <button className="btn btn-outline-light btn-lg px-4" onClick={onNavigateToLogin}>
                  <i className="bi bi-play-circle me-2"></i>Try Free
                </button>
              </div>
            </div>
            <div className="col-lg-5 text-center mt-4 mt-lg-0">
              <div className="bg-white rounded-4 p-4 shadow-lg text-dark">
                <h5 className="fw-bold mb-3" style={{ color: '#5C2597' }}>What AI Does For You</h5>
                {['Saves 15+ hours/week on admin tasks', 'Predicts fee defaults 2 weeks early', 'Generates timetables in 30 seconds', 'Auto-grades & consolidates results'].map((item, idx) => (
                  <div key={idx} className="d-flex align-items-center mb-2">
                    <i className="bi bi-check-circle-fill me-2" style={{ color: '#28A745' }}></i>
                    <span className="small">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Features Grid */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">AI Features That Transform Schools</h2>
            <p className="text-muted lead">Every module is supercharged with artificial intelligence</p>
          </div>
          <div className="row g-4">
            {aiFeatures.map((feature, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '60px', height: '60px', backgroundColor: `${feature.color}15` }}>
                      <i className={`bi ${feature.icon} fs-4`} style={{ color: feature.color }}></i>
                    </div>
                    <h5 className="card-title">{feature.title}</h5>
                    <p className="card-text text-muted">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-5">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">How AI Transforms Your School</h2>
            <p className="text-muted lead">Four pillars of intelligent school management</p>
          </div>
          <div className="row g-4">
            {workflows.map((item, idx) => (
              <div key={idx} className="col-md-6 col-lg-3">
                <div className="text-center">
                  <div className="display-4 fw-bold mb-2" style={{ color: '#5C2597', opacity: 0.2 }}>{item.step}</div>
                  <h5 className="fw-bold">{item.title}</h5>
                  <p className="text-muted small">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">Traditional Software vs AI-Powered ERP</h2>
            <p className="text-muted lead">See the difference AI makes in every area</p>
          </div>
          <div className="card border-0 shadow-sm">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead style={{ backgroundColor: '#5C2597', color: 'white' }}>
                    <tr>
                      <th className="p-3">Feature</th>
                      <th className="p-3">Traditional Software</th>
                      <th className="p-3">My Learning Board (AI)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisons.map((row, idx) => (
                      <tr key={idx}>
                        <td className="p-3 fw-semibold">{row.feature}</td>
                        <td className="p-3 text-muted">{row.traditional}</td>
                        <td className="p-3">
                          <i className="bi bi-stars me-1" style={{ color: '#FFC107' }}></i>
                          {row.ai}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4 text-center">
          <h2 className="mb-3">Ready to Bring AI to Your School?</h2>
          <p className="lead opacity-75 mb-4">Join hundreds of schools already using AI-powered management</p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <button className="btn btn-light btn-lg px-5" onClick={onNavigateToContact}>
              <i className="bi bi-telephone me-2"></i>Book Free Demo
            </button>
            <button className="btn btn-outline-light btn-lg px-5" onClick={onNavigateToLogin}>
              Start Free Trial
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-4">
        <div className="container-fluid px-4 text-center">
          <p className="mb-0 small">&copy; 2026 Omnix Digital Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AIPoweredSchoolManagement;
